import {View} from 'react-native'

import {atoms as a} from '#/alf'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {useTrendRankLabel} from '../data/labels'
import {type TrendSection} from '../data/trends'
import {CompactTrendCard} from './CompactTrendCard'
import {FeaturedTrendCard} from './FeaturedTrendCard'
import {HeroTrendCard} from './HeroTrendCard'
import {type TrendsScope} from './TrendsScopeTabs'

/**
 * The ranked "Top trends" block (`section.type === 'top'`). Unlike the uniform
 * sections, the card is chosen by rank/position rather than `section.cardStyle`:
 * #1 → hero, #2 → featured, #3+ → compact. Every card gets a green rank pill
 * ("#N Trend Globally" / "#N Trend in {region}") derived from the active scope.
 *
 * Inter-card dividers are drawn here (matching `FeedSectionView`); the outer
 * top/bottom section borders are owned by `TrendSectionView`.
 */
export function TopTrendsSection({
  section,
  scope,
  regionName,
}: {
  section: TrendSection
  scope: TrendsScope
  /** Resolved region name for the rank label when scope is regional. */
  regionName?: string
}) {
  const ss = useSemanticTokens()
  const rankLabelFor = useTrendRankLabel()

  return (
    <View style={[a.w_full]}>
      {section.trends.map((trend, i) => {
        const rank = trend.rank ?? i + 1
        const rankLabel = rankLabelFor(rank, scope, regionName)
        return (
          <View
            key={trend.id}
            style={
              i > 0
                ? [a.border_t, {borderColor: ss.border.contrast_low}]
                : undefined
            }>
            {i === 0 ? (
              <HeroTrendCard trend={trend} rankLabel={rankLabel} />
            ) : i === 1 ? (
              <FeaturedTrendCard trend={trend} rankLabel={rankLabel} />
            ) : (
              <CompactTrendCard trend={trend} rankLabel={rankLabel} />
            )}
          </View>
        )
      })}
    </View>
  )
}
